import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import ViolationCard from './ViolationCard';
import ProgressiveList from './ProgressiveList';
import './Dashboard.css';

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [minScore, setMinScore] = useState<number>(80);
  const all = useQuery(api.violations.getHighConfidenceViolations, { minScore, limit: 500 });

  const hostingReady = useMemo(() => {
    const list = (all || []).filter((v: any) => v.hostingLookupSuccess === true);
    // Group by domain, then by content title
    const byDomain: Record<string, any[]> = {};
    for (const v of list) {
      const d = String(v.domain).toLowerCase();
      (byDomain[d] ||= []).push(v);
    }
    // Sort domains by count desc then score desc
    return Object.entries(byDomain).sort((a, b) => b[1].length - a[1].length || (Math.max(...b[1].map(x=>x.confidenceScore)) - Math.max(...a[1].map(x=>x.confidenceScore))));
  }, [all]);

  // Show loading state while data is being fetched
  if (all === undefined) {
    return (
      <div className="dashboard">
        <div className="loading">Loading review queue...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-actions" style={{ marginBottom: 12 }}>
          <label>
            Min score:
            <input type="number" min={0} max={100} value={minScore} onChange={(e)=>setMinScore(Number(e.target.value)||0)} style={{ marginLeft: 8, width: 72 }} />
          </label>
        </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{all?.length ?? 0}</div>
          <div className="stat-label">High-confidence</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{hostingReady.reduce((s, [,arr])=>s+arr.length, 0)}</div>
          <div className="stat-label">Hosting-ready</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{hostingReady.length}</div>
          <div className="stat-label">Domains</div>
        </div>
      </div>

      <div className="scans-list">
        <h3>Ready for Takedown</h3>
        {hostingReady.map(([domain, items]) => {
          // Group by content title to show which scans contain violations for this domain
          const byContent: Record<string, any[]> = {};
          for (const v of items) {
            const title = v.contentTitle || 'Unknown';
            (byContent[title] ||= []).push(v);
          }
          const contentTitles = Object.keys(byContent);

          return (
            <div key={domain} className="domain-group card">
              <div className="domain-group-header">
                <div className="left">
                  <span className="domain-badge">{domain}</span>
                  <span className="domain-count">{items.length} item{items.length!==1?'s':''}</span>
                  <span className="domain-score">Top {Math.max(...items.map((v:any)=>v.confidenceScore))}%</span>
                  {contentTitles.length > 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      from {contentTitles.length} title{contentTitles.length !== 1 ? 's' : ''}: {contentTitles.slice(0, 3).join(', ')}
                      {contentTitles.length > 3 && `, +${contentTitles.length - 3} more`}
                    </span>
                  )}
                </div>
              </div>
              <div className="domain-group-body">
                <div className="domain-violations">
                  <ProgressiveList
                    items={items}
                    initial={50}
                    step={50}
                    renderItem={(violation: any) => (
                      <div key={violation._id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', padding: '0.5rem', background: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: 600, color: '#374151' }}>{violation.contentTitle}</span>
                          {violation.batchId && (
                            <button
                              onClick={() => navigate(`/scan/${violation.batchId}`)}
                              style={{ fontSize: '0.8rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              View scan →
                            </button>
                          )}
                        </div>
                        <ViolationCard violation={violation} contentTitle={violation.contentTitle || ''} />
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
